class ShoppableCarousel extends HTMLElement {
  connectedCallback() {
    this.modal = this.querySelector('[data-shoppable-modal]');
    this.panels = this.modal ? Array.from(this.modal.querySelectorAll('[data-card-panel]')) : [];
    this.currentIndex = 0;
    this.sectionId = this.dataset.sectionId;

    this.addEventListener('click', this.handleClick);
    this.modal?.addEventListener('click', this.handleModalClick);
    this.modal?.addEventListener('close', this.handleClose);
    document.addEventListener('keydown', this.handleKeydown);

    this.modal?.querySelectorAll('[data-shoppable-product]').forEach((details) => {
      this.initProduct(details);
    });

    if (window.Shopify && Shopify.designMode) {
      document.addEventListener('shopify:block:select', this.handleBlockSelect);
      document.addEventListener('shopify:block:deselect', this.handleBlockDeselect);
    }
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.handleKeydown);
    document.removeEventListener('shopify:block:select', this.handleBlockSelect);
    document.removeEventListener('shopify:block:deselect', this.handleBlockDeselect);

    if (this.pendingDeselectClose) {
      clearTimeout(this.pendingDeselectClose);
    }

    if (this.modal?.open) {
      this.modal.close();
    }

    document.body.style.overflow = '';
  }

  isOwnEvent(event) {
    const carousel = event.target?.closest?.('shoppable-carousel');
    return carousel === this;
  }

  handleBlockSelect = (event) => {
    // A new block was selected — cancel any pending deselect-close
    if (this.pendingDeselectClose) {
      clearTimeout(this.pendingDeselectClose);
      this.pendingDeselectClose = null;
    }
    if (!this.isOwnEvent(event)) return;
    // Setting changes reload the section and re-select the active block.
    // Do not reopen the modal on those reload-driven events, as doing so
    // steals focus from the setting currently being edited.
    if (event.detail?.load) return;
    const card = event.target.querySelector?.('[data-shoppable-card]');
    const index = card ? Number(card.dataset.cardIndex) || 0 : 0;
    this.open(index);
  };

  handleBlockDeselect = (event) => {
    if (!this.isOwnEvent(event)) return;
    // Defer close briefly so a follow-up block:select on a sibling block
    // can cancel it and we just switch panels instead of closing.
    if (this.pendingDeselectClose) clearTimeout(this.pendingDeselectClose);
    this.pendingDeselectClose = setTimeout(() => {
      this.pendingDeselectClose = null;
      this.close();
    }, 50);
  };

  handleClick = (event) => {
    const card = event.target.closest('[data-shoppable-card]');
    if (!card) return;
    event.preventDefault();
    this.open(Number(card.dataset.cardIndex) || 0);
  };

  handleModalClick = (event) => {
    if (event.target.closest('[data-modal-close]')) {
      this.close();
      return;
    }
    if (event.target.closest('[data-modal-prev]')) {
      event.stopPropagation();
      this.show(this.currentIndex - 1);
      return;
    }
    if (event.target.closest('[data-modal-next]')) {
      event.stopPropagation();
      this.show(this.currentIndex + 1);
      return;
    }
    if (event.target === this.modal) {
      this.close();
    }
  };

  handleClose = () => {
    document.body.style.overflow = '';
    this.modal?.classList.remove('is-open');
  };

  handleKeydown = (event) => {
    if (!this.modal?.open) return;
    if (event.key === 'ArrowLeft') this.show(this.currentIndex - 1);
    if (event.key === 'ArrowRight') this.show(this.currentIndex + 1);
  };

  open(index) {
    if (!this.modal || typeof this.modal.showModal !== 'function') return;
    this.show(index);
    this.modal.showModal();
    document.body.style.overflow = 'hidden';
    requestAnimationFrame(() => {
      this.modal.classList.add('is-open');
    });
  }

  close() {
    if (!this.modal) return;
    if (!this.modal.classList.contains('is-open')) {
      this.modal.close();
      return;
    }
    this.modal.classList.remove('is-open');
    const onEnd = () => {
      this.modal.removeEventListener('transitionend', onEnd);
      this.modal.close();
    };
    this.modal.addEventListener('transitionend', onEnd);
    setTimeout(() => {
      if (!this.modal.open) return;
      onEnd();
    }, 400);
  }

  show(index) {
    if (!this.panels.length) return;
    const total = this.panels.length;
    this.currentIndex = Math.max(0, Math.min(index, total - 1));
    this.panels.forEach((panel, i) => {
      panel.toggleAttribute('hidden', i !== this.currentIndex);
    });
    this.updateNav();
  }

  updateNav() {
    if (!this.modal) return;
    const atStart = this.currentIndex === 0;
    const atEnd = this.currentIndex === this.panels.length - 1;
    this.modal.querySelectorAll('[data-modal-prev]').forEach((btn) => {
      btn.disabled = atStart;
    });
    this.modal.querySelectorAll('[data-modal-next]').forEach((btn) => {
      btn.disabled = atEnd;
    });
  }

  initProduct(details) {
    const dataEl = details.querySelector('[data-product-data]');
    if (!dataEl) return;

    let product;
    try {
      product = JSON.parse(dataEl.textContent);
    } catch (e) {
      return;
    }

    const optionNames = (product.options || []).map((o) =>
      typeof o === 'string' ? o : o.name
    );

    const form = details.querySelector('form');
    const submitButton = form?.querySelector('button[type="submit"]');
    const defaultButtonText = submitButton?.textContent.trim();

    const onChange = () => {
      const selected = this.getSelectedOptions(details);
      const variant = (product.variants || []).find((v) =>
        optionNames.every((name, i) => v.options[i] === selected[name])
      );

      const idInput = form?.querySelector('input[name="id"]');
      if (variant && idInput) idInput.value = variant.id;

      this.updateSelectedLabels(details, selected);
      this.updatePrice(details, variant);
      this.updateAvailability(details, product, optionNames, selected);

      if (submitButton) {
        if (!variant) {
          submitButton.disabled = true;
          submitButton.textContent = 'Unavailable';
        } else if (!variant.available) {
          submitButton.disabled = true;
          submitButton.textContent = 'Sold out';
        } else {
          submitButton.disabled = false;
          if (defaultButtonText) submitButton.textContent = defaultButtonText;
        }
      }
    };

    details.querySelectorAll('input[name^="options"]').forEach((input) => {
      input.addEventListener('change', onChange);
    });

    if (form) {
      form.addEventListener('submit', (event) => this.handleAddToCart(event, form));
    }

    onChange();
  }

  getSelectedOptions(details) {
    const opts = {};
    details.querySelectorAll('input[name^="options"]:checked').forEach((input) => {
      const match = input.name.match(/options\[(.+)\]/);
      if (match) opts[match[1]] = input.value;
    });
    return opts;
  }

  updateSelectedLabels(details, selected) {
    details.querySelectorAll('[data-option-name]').forEach((fieldset) => {
      const name = fieldset.dataset.optionName;
      const target = fieldset.querySelector('.shoppable-modal__variant-selected');
      if (target && selected[name]) target.textContent = selected[name];
    });
  }

  updateAvailability(details, product, optionNames, selected) {
    const variants = product.variants || [];

    details.querySelectorAll('[data-option-name]').forEach((fieldset) => {
      const fieldsetName = fieldset.dataset.optionName;

      fieldset.querySelectorAll('input[name^="options"]').forEach((input) => {
        const candidate = { ...selected, [fieldsetName]: input.value };
        const reachable = variants.some(
          (v) =>
            optionNames.every((name, i) => v.options[i] === candidate[name]) &&
            v.available
        );
        const label = input.closest('.shoppable-modal__variant-value');
        label?.classList.toggle('is-unavailable', !reachable);
      });
    });
  }

  updatePrice(details, variant) {
    const priceEl = details.querySelector('.shoppable-modal__product-price');
    if (!priceEl || !variant) return;

    const moneyFormat =
      (window.Shopify && window.Shopify.money_format) || '${{amount}}';
    const format = typeof formatPrice === 'function'
      ? (n) => formatPrice(n, moneyFormat)
      : (n) => '$' + (n / 100).toFixed(2);

    const current = format(variant.price);
    const compare =
      variant.compare_at_price && variant.compare_at_price > variant.price
        ? format(variant.compare_at_price)
        : null;

    priceEl.innerHTML =
      (compare
        ? `<s class="shoppable-modal__product-compare">${compare}</s>`
        : '') + `<span>${current}</span>`;
  }

  async handleAddToCart(event, form) {
    event.preventDefault();
    const submitButton = form.querySelector('button[type="submit"]');
    if (submitButton?.getAttribute('aria-disabled') === 'true') return;

    submitButton?.classList.add('loading');
    submitButton?.setAttribute('aria-disabled', 'true');

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = new FormData(form);
    const cartDrawer =
      document.querySelector('cart-drawer') ||
      document.querySelector('quick-cart-drawer');

    if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
      formData.append(
        'sections',
        cartDrawer.getSectionsToRender().map((s) => s.section).join(',')
      );
      formData.append('sections_url', window.location.pathname);
    }
    config.body = formData;

    try {
      const response = await fetch(routes.cart_add_url, config);
      const data = await response.json();

      if (data.status) {
        publish(PUB_SUB_EVENTS.cartError, {
          source: 'shoppable-modal',
          productVariantId: formData.get('id'),
          errors: data.errors || data.description,
          message: data.message,
        });
        return;
      }

      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'shoppable-modal',
        productVariantId: formData.get('id'),
        cartData: data,
      });

      if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
        cartDrawer.renderContents(data);
      }
      if (typeof updateCartCounters === 'function') updateCartCounters();
      this.close();
    } catch (e) {
      console.error('[shoppable-carousel] cart add failed', e);
    } finally {
      submitButton?.classList.remove('loading');
      submitButton?.setAttribute('aria-disabled', 'false');
    }
  }
}

if (!customElements.get('shoppable-carousel')) {
  customElements.define('shoppable-carousel', ShoppableCarousel);
}
